import React, { useMemo, useState, useEffect } from 'react';
import { Card, Table, Button, Typography, Collapse, Tag, Input, Checkbox } from 'antd';
import { ArrowRightOutlined, PlusOutlined, DeleteOutlined, SettingOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useTableRenderDeferral } from '../../hooks/useTableRenderDeferral';
import TableLoadingPlaceholder from '../common/TableLoadingPlaceholder';
import { useTender } from '../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../utils/constants';
import { formatCurrency } from '../../utils/calculations';
import { useItemLibrary } from '../../hooks/useItemLibrary';
import MainLayout from '../layout/MainLayout';
import NumberInput from '../common/NumberInput';
import ExpandCollapseAllButton from '../common/ExpandCollapseAllButton';
import { ExcelImportModal } from '../common/ExcelImportExport';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import { ColumnConfigModal, ConfigSection, ConfigField, selectStyles } from '../common/ColumnConfigModal';
import type { FoulBuildUpRowData } from '../../types/tender.types';
import {
  getPOSRowDefs,
  DEFAULT_POS_SECTIONS,
  getPOSConfigurableField,
  shiftPosSectionKeys,
  POS_CONFIGURABLE_FIELDS,
  isPosGeotextileExcluded,
  POSRowType,
  POSRowDef,
} from '../../utils/posRowDefs';
import { parsePOSFromExcel } from '../../utils/posExcelUtils';
import { findPosEvaluateCode } from '../../utils/posEvaluateCodesMapping';

interface POSFlatRow {
  key: string;
  id: string;
  rowType: POSRowType;
  description: string;
  unit: string;
  itemNo: number | null;
}

interface POSSection {
  sectionNo: number;
  headerId: string;
  headerDescription: string;
  rows: POSFlatRow[];
}

// Each "PUBLIC OPEN SPACE N" section becomes its own Collapse.Panel (same
// structure as /roads-sewers/storm-sewers' Build Up tab), so its header can be
// a plain sticky element and its rows list flat with no per-row expand/collapse.
const buildPOSSections = (rowDefs: POSRowDef[]): POSSection[] => {
  const sections: POSSection[] = [];
  let current: POSSection | null = null;
  // Resets at every posHeader/subHeader/leapHeader so each visual group
  // (e.g. "POS GRAVEL PATH") numbers its own data rows from 1, rather than
  // continuing a single count across the whole sheet.
  let sectionItemCounter = 0;

  for (const row of rowDefs) {
    if (row.rowType === 'empty') continue;

    if (row.rowType === 'posHeader') {
      const match = row.id.match(/^pos_(\d+)_hdr$/);
      const sectionNo = match ? Number(match[1]) : sections.length + 1;
      current = { sectionNo, headerId: row.id, headerDescription: row.description, rows: [] };
      sections.push(current);
      sectionItemCounter = 0;
      continue;
    }

    if (row.rowType === 'subHeader' || row.rowType === 'leapHeader') {
      current?.rows.push({ key: row.id, id: row.id, rowType: row.rowType, description: row.description, unit: row.unit, itemNo: null });
      sectionItemCounter = 0;
      continue;
    }

    sectionItemCounter++;
    current?.rows.push({ key: row.id, id: row.id, rowType: row.rowType, description: row.description, unit: row.unit, itemNo: sectionItemCounter });
  }

  return sections;
};

const POS: React.FC = () => {
  const showDevMode = useDevMode();
  useScrollToTop({ behavior: 'smooth', delay: 100 });
  const { state, updateField, saveTender } = useTender();

  // Item library for dev-mode rate lookups by evaluate code — only fetched
  // when the dev-only Evaluate Code column is actually shown.
  const { data: itemLibraryData } = useItemLibrary(state.region || "", undefined, {
    enabled: showDevMode,
  });
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item.nettRate || item.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    return resourceCodeMap.get(code.trim().toUpperCase());
  };
  const navigate = useNavigate();
  useAutoSave();

  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  // Defers each section's per-item table's first mount by one paint cycle so
  // a tender with a lot of POS rows doesn't make this step look frozen while
  // the tables' initial render/layout (all sections mount immediately, even
  // collapsed) blocks the main thread - see useTableRenderDeferral's own doc
  // comment. Purely a perceived-lag fix: once it flips false the tables
  // render exactly as before.
  const isTableLoading = useTableRenderDeferral();
  const [stickyOffset, setStickyOffset] = useState(230);
  const [panelHeaderOffset, setPanelHeaderOffset] = useState(270);

  const sectionCount = state.pos?.config?.sectionCount ?? DEFAULT_POS_SECTIONS;
  // Per-section title text (pos.config.sectionTitles, header row id -> text), edited inline with the
  // pencil next to each title — same pattern as Pump Station. Shown after the default title; display only -
  // row ids, Excel import/export and the tab/page name keep the standard titles. An empty save removes it.
  const sectionTitles = state.pos?.config?.sectionTitles;
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const saveSectionTitle = (id: string) => {
    const next = { ...(sectionTitles ?? {}) };
    const trimmed = titleDraft.trim();
    if (trimmed) next[id] = trimmed;
    else delete next[id];
    updateField('pos', { ...state.pos, config: { ...state.pos?.config, sectionTitles: next } });
    setEditingTitleId(null);
  };
  const renderPosSectionTitle = (headerId: string, title: string): React.ReactNode => {
    if (editingTitleId === headerId) {
      return (
        <span onClick={(e) => e.stopPropagation()}>
          <Input
            autoFocus
            size="small"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onPressEnter={() => saveSectionTitle(headerId)}
            onBlur={() => saveSectionTitle(headerId)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditingTitleId(null); }}
            placeholder="Text to append to title"
            addonBefore={title}
            className="section-title-input"
            style={{ width: 380 }}
          />
        </span>
      );
    }
    const appended = sectionTitles?.[headerId];
    return (
      <>
        {title}
        {appended && (
          <span
            style={{
              marginLeft: 8,
              padding: '0 8px',
              borderRadius: 4,
              background: '#fff7e6',
              border: '1px solid #ffd591',
              color: '#d46b08',
              fontWeight: 600,
            }}
          >
            {appended}
          </span>
        )}
        {!state.isViewMode && (
          <EditOutlined
            title="Append text to title"
            style={{ marginLeft: 8, cursor: 'pointer', color: '#1677ff' }}
            onClick={(e) => { e.stopPropagation(); setTitleDraft(appended ?? ''); setEditingTitleId(headerId); }}
          />
        )}
      </>
    );
  };

  const POS_SECTIONS = useMemo(() => buildPOSSections(getPOSRowDefs(sectionCount)), [sectionCount]);
  const ALL_SECTION_KEYS = useMemo(() => POS_SECTIONS.map((s) => s.headerId), [POS_SECTIONS]);

  const handleAddNew = () => {
    updateField('pos', { ...state.pos, config: { ...state.pos?.config, sectionCount: sectionCount + 1 } });
  };

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [pendingDeleteSection, setPendingDeleteSection] = useState<number | null>(null);
  const requestDeleteSection = (n: number) => {
    setPendingDeleteSection(n);
    setDeleteModalVisible(true);
  };
  const cancelDeleteSection = () => {
    setDeleteModalVisible(false);
    setPendingDeleteSection(null);
  };
  const confirmDeleteSection = () => {
    if (pendingDeleteSection === null) return;
    const n = pendingDeleteSection;
    const newTable = shiftPosSectionKeys(state.pos?.table ?? {}, n);
    const newRowThicknessMm = shiftPosSectionKeys(state.pos?.config?.rowThicknessMm ?? {}, n);
    const newGeotextileIncluded = shiftPosSectionKeys(state.pos?.config?.geotextileIncluded ?? {}, n);
    const newSectionTitles = shiftPosSectionKeys(state.pos?.config?.sectionTitles ?? {}, n);
    updateField('pos', {
      ...state.pos,
      table: newTable,
      config: { ...state.pos?.config, sectionCount: Math.max(0, sectionCount - 1), rowThicknessMm: newRowThicknessMm, geotextileIncluded: newGeotextileIncluded, sectionTitles: newSectionTitles },
    });
    setDeleteModalVisible(false);
    setPendingDeleteSection(null);
  };

  const [sectionConfigModalVisible, setSectionConfigModalVisible] = useState(false);
  const [configSectionNo, setConfigSectionNo] = useState<number | null>(null);
  const [configDraft, setConfigDraft] = useState<Record<string, number>>({});
  const [configTextDraft, setConfigTextDraft] = useState<Record<string, string>>({});
  const [configGeotextileDraft, setConfigGeotextileDraft] = useState(false);
  const openSectionConfig = (n: number) => {
    const rowThicknessMm = state.pos?.config?.rowThicknessMm ?? {};
    const rowText = state.pos?.config?.rowText ?? {};
    const draft: Record<string, number> = {};
    const textDraft: Record<string, string> = {};
    POS_CONFIGURABLE_FIELDS.forEach((f) => {
      const mm = rowThicknessMm[`pos_${n}_${f.suffix}`];
      if (mm) draft[f.suffix] = mm;
      if (f.textDefault) textDraft[f.suffix] = rowText[`pos_${n}_${f.suffix}`] ?? f.textDefault;
    });
    setConfigSectionNo(n);
    setConfigDraft(draft);
    setConfigTextDraft(textDraft);
    setConfigGeotextileDraft(!!state.pos?.config?.geotextileIncluded?.[`pos_${n}_geotextile`]);
    setSectionConfigModalVisible(true);
  };
  const applySectionConfig = () => {
    if (configSectionNo === null) return;
    const n = configSectionNo;
    const nextRowThicknessMm = { ...(state.pos?.config?.rowThicknessMm ?? {}) };
    const nextRowText = { ...(state.pos?.config?.rowText ?? {}) };
    POS_CONFIGURABLE_FIELDS.forEach((f) => {
      const rowId = `pos_${n}_${f.suffix}`;
      const mm = configDraft[f.suffix];
      if (mm && mm > 0) {
        nextRowThicknessMm[rowId] = mm;
      } else {
        delete nextRowThicknessMm[rowId];
      }
      if (f.textDefault) {
        const text = configTextDraft[f.suffix]?.trim();
        if (text && text !== f.textDefault) {
          nextRowText[rowId] = text;
        } else {
          delete nextRowText[rowId];
        }
      }
    });
    const nextGeotextileIncluded = { ...(state.pos?.config?.geotextileIncluded ?? {}) };
    if (configGeotextileDraft) {
      nextGeotextileIncluded[`pos_${n}_geotextile`] = true;
    } else {
      delete nextGeotextileIncluded[`pos_${n}_geotextile`];
    }
    updateField('pos', {
      ...state.pos,
      config: { ...state.pos?.config, rowThicknessMm: nextRowThicknessMm, rowText: nextRowText, geotextileIncluded: nextGeotextileIncluded },
    });
    setSectionConfigModalVisible(false);
  };

  useEffect(() => {
    const el = document.querySelector('.pos-sticky-header') as HTMLElement | null;
    const base = 144 + (el?.offsetHeight ?? 0);
    setStickyOffset(base);
    const headerRowEl = document.querySelector('.pos-shared-header-wrapper') as HTMLElement | null;
    setPanelHeaderOffset(base + (headerRowEl?.offsetHeight ?? 0));
  }, []);

  const savedData = useMemo(() => state.pos?.table ?? {}, [state.pos]);
  const rowThicknessMm = state.pos?.config?.rowThicknessMm ?? {};
  const rowText = state.pos?.config?.rowText ?? {};
  const geotextileIncluded = state.pos?.config?.geotextileIncluded;

  const getRow = (rowId: string): FoulBuildUpRowData =>
    savedData[rowId] ?? { quant: null, evaluateCode: '' };

  const updateRow = (rowId: string, changes: Partial<FoulBuildUpRowData>) => {
    const current = savedData[rowId] ?? { quant: null, evaluateCode: '' };
    updateField('pos', { ...state.pos, table: { ...savedData, [rowId]: { ...current, ...changes } } });
  };

  const columns = useMemo(() => [
    {
      title: 'Item',
      key: 'itemNo',
      width: 60,
      align: 'center' as const,
      fixed: 'left' as const,
      render: (_: unknown, record: POSFlatRow) => {
        if (record.rowType !== 'data') return null;
        return record.itemNo !== null ? (
          <span style={{ color: '#000000', fontSize: 12 }}>{record.itemNo}</span>
        ) : null;
      },
    },
    {
      title: 'Description',
      key: 'description',
      width: 480,
      fixed: 'left' as const,
      render: (_: unknown, record: POSFlatRow) => {
        if (record.rowType === 'subHeader' || record.rowType === 'leapHeader') {
          return (
            <Typography.Text strong className="text-indigo-600">
              {record.description}
            </Typography.Text>
          );
        }
        const configurableField = getPOSConfigurableField(record.id);
        const mm = configurableField ? rowThicknessMm[record.id] : undefined;
        const customText = configurableField ? rowText[record.id] : undefined;
        if (configurableField && (mm || customText)) {
          return <Typography.Text>{configurableField.describe(mm ?? configurableField.placeholderMm ?? 'xx', customText)}</Typography.Text>;
        }
        if (isPosGeotextileExcluded(record.id, geotextileIncluded ?? {})) {
          return (
            <Typography.Text>
              {record.description}
            </Typography.Text>
          );
        }
        return <Typography.Text>{record.description}</Typography.Text>;
      },
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: POSFlatRow) => {
        if (record.rowType !== 'data') return null;
        const row = getRow(record.id);
        return (
          <NumberInput
            value={row.quant ?? 0}
            onChange={(v) => updateRow(record.id, { quant: v })}
            decimalPlaces={3}
            positiveOnly
            placeholder="0.000"
            style={{ width: '100%', textAlign: 'right' }}
          />
        );
      },
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, record: POSFlatRow) => {
        if (!record.unit) return null;
        return <span style={{ color: '#6b7280' }}>{record.unit}</span>;
      },
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      key: 'evaluateCode',
      width: 160,
      render: (_: unknown, record: POSFlatRow) => {
        if (record.rowType !== 'data') return null;
        const configurableField = getPOSConfigurableField(record.id);
        const mm = configurableField ? rowThicknessMm[record.id] : undefined;
        const customText = configurableField ? rowText[record.id] : undefined;
        const description = configurableField && (mm || customText)
          ? configurableField.describe(mm ?? configurableField.placeholderMm ?? 'xx', customText)
          : record.description;
        const code = findPosEvaluateCode(description);
        if (!code) return <Typography.Text type="secondary">—</Typography.Text>;
        const libRate = getLibRate(code);
        return (
          <span>
            <Tag color="blue">{code}</Tag>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
    {
      title: 'Action',
      key: 'action',
      width: 90,
      align: 'center' as const,
      render: () => null,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [savedData, rowThicknessMm, rowText, geotextileIncluded, showDevMode, resourceCodeMap]);

  const handleImport = async (file: File) => {
    setImportLoading(true);
    try {
      const imported = await parsePOSFromExcel(file);
      updateField('pos', {
        ...state.pos,
        table: imported.table,
        config: { ...state.pos?.config, sectionCount: imported.config.sectionCount, rowThicknessMm: imported.config.rowThicknessMm, sectionTitles: imported.config.sectionTitles },
      });
      setImportModalVisible(false);
      const count = Object.keys(imported.table).length;
      if (count === 0) {
        alert('No matching rows found in "POS" sheet. Check that the sheet exists and "PUBLIC OPEN SPACE" section headers are present.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('POS import failed:', err);
      alert(`Import failed: ${msg}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSaveAndContinue = () => {
    updateField("status", "In-progress");
    updateField("boqStatus", "In-progress");
    updateField("lastModified", new Date());
    saveTender();
    navigate('/roads-sewers/review');
  };

  const allExpanded = activeKeys.length === ALL_SECTION_KEYS.length;

  return (
    <MainLayout showFooter={false}>
      <Card
        className="pos-card shadow-sm"
        styles={{ body: { padding: 0 } }}
        style={{ borderRadius: '8px', overflow: 'visible' }}
      >
        {/* Sticky header */}
        <div className="pos-sticky-header" style={{ position: 'sticky', top: '144px', backgroundColor: '#fff', zIndex: 98, borderRadius: '8px 8px 0 0', isolation: 'isolate' }}>
        <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <div className="flex justify-between items-center">
            <div>
              <Typography.Title level={3} style={{ fontWeight: 600, fontSize: '20px', color: '#111827', marginBottom: '8px' }}>
                POS
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: '14px', color: '#6B7280' }}>
                Public Open Space quantities
              </Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExpandCollapseAllButton
                expanded={allExpanded}
                onToggle={() => setActiveKeys(allExpanded ? [] : ALL_SECTION_KEYS)}
              />
              {showDevMode && (
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => setImportModalVisible(true)}
                  style={{ color: '#1890ff', fontWeight: 500 }}
                >
                  Import Take-off
                </Button>
              )}
            </div>
          </div>
        </div>
        </div>{/* end pos-sticky-header */}

        {/* Content */}
        <div className="px-6 py-6">
          {/* Shared table header — every accordion table uses the same columns,
              so render the header once, sticky above the accordion list, instead
              of repeating it inside each panel. */}
          <div className="pos-shared-header-wrapper" style={{ position: 'sticky', top: `${stickyOffset}px`, zIndex: 95 }}>
            <Table<POSFlatRow>
              className="pos-shared-header"
              bordered
              size="middle"
              showHeader
              pagination={false}
              rowKey="key"
              dataSource={[]}
              columns={columns}
              locale={{ emptyText: '' }}
            />
          </div>
          <Collapse
            activeKey={activeKeys}
            onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys as string[] : keys ? [keys as string] : [])}
            className="pos-collapse"
            size="large"
            bordered={false}
            expandIconPosition="start"
            expandIcon={({ isActive }) => (
              <span className={`pos-expand-icon${isActive ? '' : ' pos-expand-icon-collapsed'}`} />
            )}
          >
            {POS_SECTIONS.map((section) => (
              <Collapse.Panel
                key={section.headerId}
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography.Text strong className="text-indigo-600">{renderPosSectionTitle(section.headerId, section.headerDescription)}</Typography.Text>
                    <span
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: 'flex', justifyContent: 'center', width: 90 }}
                    >
                      <Button
                        type="text"
                        size="small"
                        className="configure-headers-btn"
                        icon={<SettingOutlined style={{ color: COLORS.primary }} />}
                        onClick={() => openSectionConfig(section.sectionNo)}
                      />
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => requestDeleteSection(section.sectionNo)}
                      />
                    </span>
                  </div>
                }
              >
                {isTableLoading ? (
                  <TableLoadingPlaceholder />
                ) : (
                  <Table<POSFlatRow>
                    className="pos-table"
                    bordered
                    size="middle"
                    showHeader={false}
                    pagination={false}
                    rowKey="key"
                    dataSource={section.rows}
                    columns={columns}
                    rowClassName={(record) => {
                      if (record.rowType === 'subHeader') return 'pos-row-sub-header';
                      if (record.rowType === 'leapHeader') return 'pos-row-leap-header';
                      return '';
                    }}
                  />
                )}
              </Collapse.Panel>
            ))}
          </Collapse>

          {/* Add New Public Open Space */}
          <div style={{ marginTop: '16px' }}>
            <Button
              type="text"
              icon={<PlusOutlined style={{ color: COLORS.primary }} />}
              onClick={handleAddNew}
              style={{
                color: COLORS.primary,
                fontWeight: 500,
                padding: '4px 8px'
              }}
            >
              Add New POS
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      <ExcelImportModal
        onImport={handleImport}
        onDownloadTemplate={() => {/* template not needed */}}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
      />

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDeleteSection}
        onCancel={cancelDeleteSection}
        title={pendingDeleteSection !== null ? `Delete PUBLIC OPEN SPACE ${pendingDeleteSection}?` : undefined}
        message="This removes all quantities entered for this section. This action can't be undone."
      />

      <ColumnConfigModal
        title={configSectionNo !== null ? `Configure PUBLIC OPEN SPACE ${configSectionNo}` : 'Configure'}
        open={sectionConfigModalVisible}
        onClose={() => setSectionConfigModalVisible(false)}
        onApply={applySectionConfig}
        width={600}
        isViewMode={state.isViewMode}
      >
        {(() => {
          const grouped: { title: string; fields: typeof POS_CONFIGURABLE_FIELDS }[] = [];
          POS_CONFIGURABLE_FIELDS.forEach((f) => {
            let group = grouped.find((g) => g.title === f.section);
            if (!group) {
              group = { title: f.section, fields: [] };
              grouped.push(group);
            }
            group.fields.push(f);
          });
          return (<>
            {grouped.map((group, idx) => (
            <ConfigSection key={group.title} title={group.title} columns={2} showDivider={idx < grouped.length - 1}>
              {group.title === 'Main' && (
                <div className="col-span-2">
                  <Checkbox checked={configGeotextileDraft} onChange={(e) => setConfigGeotextileDraft(e.target.checked)}>
                    Geotextile membrane
                  </Checkbox>
                </div>
              )}
              {group.fields.map((f) => (
                <ConfigField key={f.suffix} label={f.label}>
                  {f.textDefault ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <NumberInput
                        value={(configDraft[f.suffix] ?? 0) / 1000}
                        onChange={(v) => {
                          const metresValue = v ? parseFloat(v.toString()) : 0;
                          const mmValue = metresValue > 0 ? Math.round(metresValue * 1000) : 0;
                          setConfigDraft((prev) => ({ ...prev, [f.suffix]: mmValue }));
                        }}
                        positiveOnly
                        decimalPlaces={3}
                        zeroIntegerOnly
                        style={{ width: 80, flexShrink: 0 }}
                      />
                      <span style={{ color: '#6b7280' }}>-</span>
                      <Input
                        value={configTextDraft[f.suffix] ?? f.textDefault}
                        onChange={(e) => setConfigTextDraft((prev) => ({ ...prev, [f.suffix]: e.target.value }))}
                        placeholder={f.textDefault}
                        style={{ flex: 1 }}
                      />
                    </div>
                  ) : f.options ? (
                    <select
                      value={configDraft[f.suffix] ?? ''}
                      onChange={(e) => setConfigDraft((prev) => ({ ...prev, [f.suffix]: e.target.value === '' ? 0 : Number(e.target.value) }))}
                      style={selectStyles}
                    >
                      <option value="">Select</option>
                      {f.options.map((mm) => (
                        <option key={mm} value={mm}>{f.describe(mm)}</option>
                      ))}
                    </select>
                  ) : (
                    <NumberInput
                      value={(configDraft[f.suffix] ?? 0) / 1000}
                      onChange={(v) => {
                        const metresValue = v ? parseFloat(v.toString()) : 0;
                        const mmValue = metresValue > 0 ? Math.round(metresValue * 1000) : 0;
                        setConfigDraft((prev) => ({ ...prev, [f.suffix]: mmValue }));
                      }}
                      positiveOnly
                      decimalPlaces={3}
                      zeroIntegerOnly
                      style={{ width: '100%' }}
                    />
                  )}
                </ConfigField>
              ))}
            </ConfigSection>
          ))}
          </>);
        })()}
      </ColumnConfigModal>

      <style>{`
        .pos-card { overflow: visible !important; }
        .pos-card .ant-card-body { overflow: visible !important; }
        .pos-card .ant-table-tbody > tr > td { vertical-align: middle; }
        .pos-sticky-header { box-shadow: none; will-change: transform; transform: translateZ(0); z-index: 98 !important; }

        /* Modern accordion: each section is its own rounded, gently-shadowed
           card with spacing between them, instead of antd's default flat
           bordered-list Collapse look. */
        .pos-collapse { background: transparent; }
        .pos-collapse > .ant-collapse-item {
          margin-bottom: 0;
          background: #fff;
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
          transition: box-shadow 0.15s ease, border-color 0.15s ease;
        }
        .pos-collapse > .ant-collapse-item:first-child {
          border-top: 1px solid #e5e7eb !important;
          border-radius: 0 0 0 0 !important;
        }
        .pos-collapse > .ant-collapse-item:hover {
          border-color: #c7d2fe !important;
        }
        .pos-collapse .ant-collapse-content-box { padding: 0 !important; }
        .pos-collapse .ant-collapse-content {
          border-top: 1px solid #e5e7eb !important;
          border-radius: 0;
        }
        /* Sticky accordion panel headers — must sit above table cell stacking
           contexts, but below StepperNavigation.tsx's fixed bar (z-index: 99,
           top: 88px) so it doesn't paint through the stepper while scrolling. */
        .pos-collapse .ant-collapse-header {
          position: sticky !important;
          top: ${panelHeaderOffset}px !important;
          z-index: 90 !important;
          align-items: center !important;
          padding: 14px 20px !important;
          background-color: #eef2ff !important;
          border-radius: 0 !important;
        }
        .pos-collapse .ant-collapse-item-active > .ant-collapse-header {
          border-bottom: 1px solid #e0e7ff !important;
        }
        /* Replicates antd Table's own row-expand-icon (+/- square), matching the
           Foul Summary table's expand icon. Built standalone rather than reusing
           antd's .ant-table-row-expand-icon class, since that rule is scoped
           under .ant-table-wrapper and does not apply outside a real Table. */
        .pos-expand-icon {
          position: relative;
          display: inline-block;
          width: 17px;
          height: 17px;
          background: #fff;
          border: 1px solid #d9d9d9;
          border-radius: 50%;
          color: rgba(0, 0, 0, 0.88);
          cursor: pointer;
          transform: scale(0.94);
        }
        .pos-expand-icon::before,
        .pos-expand-icon::after {
          content: '';
          position: absolute;
          background: currentcolor;
          transition: transform 0.2s ease-out;
        }
        .pos-expand-icon::before {
          top: 7px;
          left: 1px;
          right: 1px;
          height: 1px;
        }
        .pos-expand-icon::after {
          top: 1px;
          bottom: 1px;
          left: 7px;
          width: 1px;
          transform: rotate(90deg);
        }
        .pos-expand-icon-collapsed::before {
          transform: rotate(-180deg);
        }
        .pos-expand-icon-collapsed::after {
          transform: rotate(0deg);
        }
        .pos-table {
          border-radius: 0;
          overflow: hidden;
        }
        .pos-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
          /* Reset stacking-context promoters so these non-sticky headers don't
             paint over the sticky accordion header */
          z-index: 2 !important;
          will-change: auto !important;
          transform: none !important;
        }
        .pos-row-sub-header > td {
          background-color: #eff6ff !important;
        }
        .pos-row-leap-header > td {
          background-color: #eff6ff !important;
        }
        /* Shared header row: renders columns/widths once via a real antd
           Table (kept in sync with the per-panel Tables' columns prop), body
           hidden so only the <thead> shows. */
        .pos-shared-header-wrapper { background-color: #fff; }
        .pos-shared-header .ant-table-tbody,
        .pos-shared-header .ant-table-placeholder {
          display: none !important;
        }
        .pos-shared-header .ant-table {
          border-radius: 0;
          overflow: hidden;
        }
        .pos-shared-header .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
        }
      `}</style>
    </MainLayout>
  );
};

export default POS;
